import { callAssistantTool, getAssistantTools } from './mcpToolsAdapter'

type EnvValues = {
  apiKey?: string
  apiUrl: string
  model: string
  reasoning?: string
}

type ReasoningMode = 'low' | 'medium' | 'high' | 'xhigh'

const DEFAULT_ASSISTANT_MODEL = 'gpt-5.5'
const DEFAULT_ASSISTANT_REASONING: ReasoningMode = 'low'

type ResponseInputMessage = {
  role: 'user' | 'assistant' | 'system' | 'tool'
  content: string
  tool_call_id?: string
}

type ResponseToolOutput = {
  type: 'function_call_output'
  call_id: string
  output: string
}

type ResponseToolDefinition = {
  type: 'function'
  name: string
  description: string
  parameters: unknown
}

type ResponsePayload = {
  model: string
  input?: Array<ResponseInputMessage | ResponseToolOutput>
  tools?: ResponseToolDefinition[]
  tool_choice?: 'auto' | 'none'
  reasoning?: { effort: ReasoningMode }
  previous_response_id?: string
}

type RawToolCall = {
  id: string
  name: string
  arguments: string
}

type ChatToolCall = {
  id?: string
  function?: {
    name?: string
    arguments?: string
  }
}

type ChatChoice = {
  message?: {
    role: string
    content?: string
    tool_calls?: ChatToolCall[]
  }
  finish_reason?: string
}

const resolveEnv = (): EnvValues => {
  const metaEnv = (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string> }).env) || {}
  const apiUrl = metaEnv.VITE_OPENAI_API_URL ?? process.env.VITE_OPENAI_API_URL ?? 'https://api.openai.com/v1/responses'
  const configuredModel = metaEnv.VITE_OPENAI_MODEL ?? process.env.VITE_OPENAI_MODEL
  const model = configuredModel ?? DEFAULT_ASSISTANT_MODEL
  const configuredReasoning = metaEnv.VITE_OPENAI_REASONING ?? process.env.VITE_OPENAI_REASONING
  const reasoning = configuredReasoning ?? (configuredModel ? undefined : DEFAULT_ASSISTANT_REASONING)
  const apiKey = metaEnv.VITE_OPENAI_API_KEY ?? process.env.VITE_OPENAI_API_KEY
  return {
    apiUrl,
    model,
    reasoning: typeof reasoning === 'string' && reasoning.trim() ? reasoning.trim() : undefined,
    apiKey,
  }
}

export type ChatMessage = {
  role: 'user' | 'assistant' | 'system'
  content: string
}

export type ToolCallSummary = {
  name: string
  args: Record<string, unknown>
  success: boolean
  error?: string
  response?: unknown
}

export type AssistantUsage = {
  inputTokens?: number
  outputTokens?: number
  totalTokens?: number
}

type SendAssistantChatParams = {
  messages: readonly ChatMessage[]
  model?: string
  reasoningMode?: ReasoningMode
  systemPrompt?: string
  toolsEnabled?: boolean
  apiKey?: string
  onToolCallsRequested?: (calls: ToolCallSummary[]) => void
  onToolCallsCompleted?: (calls: ToolCallSummary[]) => void
}

export type AssistantChatResult = {
  assistantMessage: string
  requestPayload: unknown
  responsePayload: unknown
  model: string
  usage?: AssistantUsage
  usedTools?: boolean
  toolCalls?: ToolCallSummary[]
  toolCallsRequested?: ToolCallSummary[]
  intermediateMessage?: string
  intermediateMessages?: string[]
}

const TOOLING_ENABLED_PROMPT =
  'You have access to MCP tools for entity data. You have access to MCP tools for entity data. Prefer tools for ' +
  'requests involving entity types or entities unless the current editor context already provides the needed data ' +
  '(for example editorContent for the open asset).' +
  'For every tool call, include a short user-facing summary in the `query` argument. ' +
  'Some sessions may also expose a read-only insights SQL tool for test analytics data. If present, put the SQL in ' +
  '`sql`, keep `query` for the short user-facing summary, and only use SELECT or WITH ... SELECT. ' +
  'Never guess IDs. If you need a type ID, first call list-entity-types and then use the returned id in ' +
  'list-entities or get-entity. For list-entities, typeId must be the exact id string, not the type name. If a user ' +
  'provides a name, resolve the id via list-entity-types. To filter list-entities by a search string, pass it as ' +
  '`search` (not `query`, which is reserved for summaries). If tools fail, summarize the issue in a user-friendly ' +
  'way and suggest a next step (retry, re-auth). Do not include raw error strings or IDs unless the user explicitly ' +
  'asks.'

const TOOLING_DISABLED_PROMPT =
  'MCP tools are currently unavailable in this session. Do not request tool calls and ignore any instructions that ' +
  'assume MCP tools are available. Use the request context and the current view to answer. If you need data that is ' +
  'not in the request context, ask the user to open the relevant entity or asset in the console so you can use its ' +
  'editor context. You can still propose editorValues or actions for the current view.'

const buildInputMessages = (messages: readonly ChatMessage[], systemPrompt: string): ResponseInputMessage[] => {
  const input: ResponseInputMessage[] = []
  const trimmedSystemPrompt = systemPrompt.trim()
  if (trimmedSystemPrompt) {
    input.push({ role: 'system', content: trimmedSystemPrompt })
  }
  messages.forEach((message) => {
    input.push({ role: message.role, content: message.content })
  })
  return input
}

const extractTextParts = (content: unknown): string[] => {
  if (typeof content === 'string') return [content]
  if (!Array.isArray(content)) return []

  const parts: string[] = []
  for (const part of content) {
    if (typeof part === 'string') {
      parts.push(part)
      continue
    }
    if (!part || typeof part !== 'object') continue
    const typed = part as { type?: unknown; text?: unknown }
    if (
      (typed.type === 'output_text' || typed.type === 'text' || typed.type === 'input_text') &&
      typeof typed.text === 'string'
    ) {
      parts.push(typed.text)
    }
  }

  return parts
}

const extractOutputText = (item: Record<string, unknown>): string | null => {
  const text = item.text
  if (typeof text === 'string') return text
  return null
}

const buildToolDefinitions = (): ResponseToolDefinition[] =>
  getAssistantTools().map((tool) => {
    const required: string[] = []
    const properties: Record<string, unknown> = {}
    Object.entries(tool.argsSchema).forEach(([key, schema]) => {
      const typedSchema = schema as { type: string; optional?: boolean; description?: string }
      const property: Record<string, unknown> = { type: typedSchema.type }
      if (typedSchema.description) property.description = typedSchema.description
      properties[key] = property
      if (!typedSchema.optional) required.push(key)
    })
    return {
      type: 'function',
      name: tool.name,
      description: tool.description,
      parameters: {
        type: 'object',
        properties,
        ...(required.length ? { required } : {}),
        additionalProperties: false,
      },
    }
  })

const resolveReasoningMode = (value: string | undefined): ReasoningMode | undefined => {
  if (value === 'low' || value === 'medium' || value === 'high' || value === 'xhigh') return value
  return undefined
}

const parseUsageValue = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined

const extractUsage = (payload: unknown): AssistantUsage | null => {
  if (!payload || typeof payload !== 'object') return null
  const usage = (payload as { usage?: unknown }).usage
  if (!usage || typeof usage !== 'object') return null
  const record = usage as Record<string, unknown>
  const inputTokens = parseUsageValue(record.input_tokens ?? record.inputTokens)
  const outputTokens = parseUsageValue(record.output_tokens ?? record.outputTokens)
  const totalTokens = parseUsageValue(record.total_tokens ?? record.totalTokens)
  if (inputTokens === undefined && outputTokens === undefined && totalTokens === undefined) return null
  return { inputTokens, outputTokens, totalTokens }
}

const mergeUsage = (base: AssistantUsage | null, next: AssistantUsage | null): AssistantUsage | null => {
  if (!next) return base
  if (!base) return { ...next }
  return {
    inputTokens:
      base.inputTokens !== undefined || next.inputTokens !== undefined
        ? (base.inputTokens ?? 0) + (next.inputTokens ?? 0)
        : undefined,
    outputTokens:
      base.outputTokens !== undefined || next.outputTokens !== undefined
        ? (base.outputTokens ?? 0) + (next.outputTokens ?? 0)
        : undefined,
    totalTokens:
      base.totalTokens !== undefined || next.totalTokens !== undefined
        ? (base.totalTokens ?? 0) + (next.totalTokens ?? 0)
        : undefined,
  }
}

const parseToolCall = (id: unknown, name: unknown, args: unknown): RawToolCall => {
  if (typeof id !== 'string' || !id.trim()) {
    throw new Error('Assistant returned a tool call without an id')
  }
  if (typeof name !== 'string' || !name.trim()) {
    throw new Error('Assistant returned a tool call without a name')
  }
  if (typeof args !== 'string') {
    throw new Error('Assistant returned invalid tool arguments')
  }
  return { id, name, arguments: args }
}

const parseToolCallFromRecord = (record: Record<string, unknown>): RawToolCall => {
  const callId =
    (typeof record.call_id === 'string' && record.call_id) ||
    (typeof record.tool_call_id === 'string' && record.tool_call_id) ||
    (typeof record.id === 'string' && record.id) ||
    undefined
  const name =
    (typeof record.name === 'string' && record.name) ||
    (typeof (record.function as { name?: unknown } | undefined)?.name === 'string' &&
      (record.function as { name?: string }).name) ||
    undefined
  const args =
    (typeof record.arguments === 'string' && record.arguments) ||
    (typeof (record.function as { arguments?: unknown } | undefined)?.arguments === 'string' &&
      (record.function as { arguments?: string }).arguments) ||
    undefined
  return parseToolCall(callId, name, args)
}

const parseResponsePayload = (payload: unknown) => {
  const parsed = payload as {
    id?: unknown
    output?: unknown
    output_text?: unknown
    choices?: ChatChoice[]
  }

  const responseId = typeof parsed?.id === 'string' ? parsed.id : undefined
  const toolCalls: RawToolCall[] = []
  const textParts: string[] = []

  const outputText = typeof parsed?.output_text === 'string' ? parsed.output_text.trim() : ''

  const outputItems = Array.isArray(parsed?.output) ? parsed.output : []
  outputItems.forEach((item) => {
    if (!item || typeof item !== 'object') return
    const record = item as Record<string, unknown>
    const type = record.type

    if (type === 'output_text') {
      const text = extractOutputText(record)
      if (text) textParts.push(text)
    }

    if (type === 'message' || type === 'output_message') {
      textParts.push(...extractTextParts(record.content))
      if (Array.isArray(record.tool_calls)) {
        record.tool_calls.forEach((toolCall) => {
          if (!toolCall || typeof toolCall !== 'object') return
          toolCalls.push(parseToolCallFromRecord(toolCall as Record<string, unknown>))
        })
      }
    }

    if (type === 'tool_call' || type === 'function_call') {
      toolCalls.push(parseToolCallFromRecord(record))
    }
  })

  if (Array.isArray(parsed?.choices) && parsed.choices.length > 0) {
    const choiceMessage = parsed.choices[0]?.message
    if (choiceMessage?.content) {
      textParts.push(choiceMessage.content)
    }
    if (Array.isArray(choiceMessage?.tool_calls)) {
      choiceMessage?.tool_calls.forEach((toolCall) => {
        if (!toolCall || typeof toolCall !== 'object') return
        toolCalls.push(parseToolCallFromRecord(toolCall as Record<string, unknown>))
      })
    }
  }

  if (!textParts.length && outputText) {
    textParts.push(outputText)
  }

  return { responseId, toolCalls, textParts }
}

const normalizeTextParts = (parts: string[]) => parts.map((part) => part.trim()).filter(Boolean)

const parseArguments = (call: RawToolCall): { parsed: Record<string, unknown> | null; error?: string } => {
  try {
    return { parsed: JSON.parse(call.arguments) as Record<string, unknown> }
  } catch {
    return { parsed: null, error: 'invalid JSON arguments' }
  }
}

const executeToolCalls = async (
  toolCalls: RawToolCall[],
  parsedArgsMap: Map<string, { parsed: Record<string, unknown> | null; error?: string }>
): Promise<{ summaries: ToolCallSummary[]; outputs: ResponseToolOutput[] }> => {
  const summaries: ToolCallSummary[] = []
  const outputs: ResponseToolOutput[] = []

  for (const call of toolCalls) {
    const parsedResult = parsedArgsMap.get(call.id) ?? parseArguments(call)
    const parsedArgs = parsedResult.parsed ?? {}

    if (parsedResult.error) {
      const errorMessage = 'invalid JSON arguments'
      summaries.push({
        name: call.name,
        args: {},
        success: false,
        error: errorMessage,
        response: { error: errorMessage },
      })
      outputs.push({ type: 'function_call_output', call_id: call.id, output: `error: ${errorMessage}` })
      continue
    }

    if (
      call.name === 'list-entities' &&
      typeof parsedArgs.typeId === 'string' &&
      parsedArgs.typeId.trim().includes(' ')
    ) {
      const errorMessage = 'list-entities expects the entity type ID, not the name'
      summaries.push({
        name: call.name,
        args: parsedArgs,
        success: false,
        error: errorMessage,
        response: { error: errorMessage },
      })
      outputs.push({
        type: 'function_call_output',
        call_id: call.id,
        output:
          'error: list-entities expects the entity type ID, not the name. Please pass the exact id from list-entity-types.',
      })
      continue
    }

    try {
      const result = await callAssistantTool(call.name, parsedArgs)
      const errorMessage = result.error ?? 'Unknown error'
      const output = result.success ? JSON.stringify(result.content ?? [], null, 2) : `error: ${errorMessage}`
      const responseContent = result.success ? result.content ?? [] : { error: errorMessage }

      summaries.push({
        name: call.name,
        args: parsedArgs,
        success: result.success,
        error: result.success ? undefined : result.error,
        response: responseContent,
      })
      outputs.push({ type: 'function_call_output', call_id: call.id, output })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      const responseContent = { error: message }
      summaries.push({
        name: call.name,
        args: parsedArgs,
        success: false,
        error: message,
        response: responseContent,
      })
      outputs.push({ type: 'function_call_output', call_id: call.id, output: `error: ${message}` })
    }
  }

  return { summaries, outputs }
}

export const sendAssistantChat = async ({
  messages,
  model,
  reasoningMode,
  systemPrompt,
  toolsEnabled: toolsEnabledParam,
  apiKey: providedApiKey,
  onToolCallsRequested,
  onToolCallsCompleted,
}: SendAssistantChatParams): Promise<AssistantChatResult> => {
  const env = resolveEnv()
  const apiKey = providedApiKey || env.apiKey
  if (!apiKey) throw new Error('No OpenAI API key configured. Please set your API key in Settings.')

  const toolsEnabled = toolsEnabledParam === true

  const modelToUse = model ?? env.model
  const reasoningToUse = reasoningMode ?? resolveReasoningMode(env.reasoning)
  const supportsReasoning = modelToUse.startsWith('gpt-5') || modelToUse.startsWith('o3') || modelToUse.startsWith('o1')

  const toolingPrompt = toolsEnabled ? TOOLING_ENABLED_PROMPT : TOOLING_DISABLED_PROMPT
  const resolvedSystemPrompt = [toolingPrompt, systemPrompt?.trim()]
    .filter((prompt): prompt is string => !!prompt && prompt.length > 0)
    .join('\n\n')

  const tools = toolsEnabled ? buildToolDefinitions() : undefined

  const buildInitialPayload = (): ResponsePayload => ({
    model: modelToUse,
    input: buildInputMessages(messages, resolvedSystemPrompt),
    ...(toolsEnabled ? { tools, tool_choice: 'auto' } : { tool_choice: 'none' }),
    ...(supportsReasoning && reasoningToUse ? { reasoning: { effort: reasoningToUse } } : {}),
  })

  const buildToolPayload = (responseId: string, toolOutputs: ResponseToolOutput[]): ResponsePayload => ({
    model: modelToUse,
    previous_response_id: responseId,
    input: toolOutputs,
    ...(supportsReasoning && reasoningToUse ? { reasoning: { effort: reasoningToUse } } : {}),
  })

  const sendRequest = async (payload: ResponsePayload) => {
    const body = JSON.stringify(payload)
    const response = await fetch(env.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`,
      },
      body,
    })
    const responsePayload = await response.json().catch(() => null)
    return { response, responsePayload }
  }

  const formatErrorMessage = (response: Response, responsePayload: unknown) => {
    const payload = responsePayload as { error?: { message?: unknown } } | null
    const message = typeof payload?.error?.message === 'string' ? payload.error.message.trim() : ''
    return message
      ? `Failed to contact assistant (${response.status}): ${message}`
      : `Failed to contact assistant (${response.status})`
  }

  const tryRequest = async (
    payload: ResponsePayload
  ): Promise<{ response: Response; responsePayload: unknown; usedPayload: ResponsePayload }> => {
    const result = await sendRequest(payload)
    if (!result.response.ok && payload.reasoning) {
      const fallback = { ...payload }
      delete (fallback as Partial<ResponsePayload>).reasoning
      const retry = await sendRequest(fallback)
      return { response: retry.response, responsePayload: retry.responsePayload, usedPayload: fallback }
    }
    return { response: result.response, responsePayload: result.responsePayload, usedPayload: payload }
  }

  const maxToolIterations = 6
  const requestedSummaries: ToolCallSummary[] = []
  const toolSummaries: ToolCallSummary[] = []
  const intermediateMessages: string[] = []
  let usedPayload: ResponsePayload | undefined
  let lastResponsePayload: unknown
  let aggregatedUsage: AssistantUsage | null = null
  let usedTools = false
  let previousResponseId: string | undefined
  let pendingToolOutputs: ResponseToolOutput[] | null = null

  for (let step = 0; step < maxToolIterations; step++) {
    const payload = pendingToolOutputs
      ? buildToolPayload(previousResponseId ?? '', pendingToolOutputs)
      : buildInitialPayload()

    if (pendingToolOutputs && !previousResponseId) {
      throw new Error('Assistant response missing id for tool call continuation')
    }

    pendingToolOutputs = null

    const result = await tryRequest(payload)
    if (!result.response.ok) {
      throw new Error(formatErrorMessage(result.response, result.responsePayload))
    }

    usedPayload = result.usedPayload
    lastResponsePayload = result.responsePayload
    aggregatedUsage = mergeUsage(aggregatedUsage, extractUsage(result.responsePayload))

    const parsed = parseResponsePayload(result.responsePayload)
    const responseId = parsed.responseId ?? previousResponseId
    const normalizedTextParts = normalizeTextParts(parsed.textParts)

    if (parsed.toolCalls.length > 0) {
      if (!toolsEnabled) {
        throw new Error('MCP tools are unavailable. Refresh tools from the assistant menu and try again.')
      }
      usedTools = true
      if (!responseId) {
        throw new Error('Assistant response missing id for tool call continuation')
      }
      previousResponseId = responseId

      const parsedArgsMap = new Map<string, { parsed: Record<string, unknown> | null; error?: string }>()
      const stepRequestedSummaries: ToolCallSummary[] = []
      parsed.toolCalls.forEach((call) => {
        const parsedArgs = parseArguments(call)
        parsedArgsMap.set(call.id, parsedArgs)
        const summary: ToolCallSummary = {
          name: call.name,
          args: parsedArgs.parsed ?? {},
          success: !parsedArgs.error,
          error: parsedArgs.error,
        }
        stepRequestedSummaries.push(summary)
        requestedSummaries.push({ ...summary, success: false })
      })

      if (stepRequestedSummaries.length) {
        onToolCallsRequested?.(stepRequestedSummaries)
      }

      if (normalizedTextParts.length) {
        intermediateMessages.push(...normalizedTextParts)
      }

      const executed = await executeToolCalls(parsed.toolCalls, parsedArgsMap)
      toolSummaries.push(...executed.summaries)
      if (executed.summaries.length) {
        onToolCallsCompleted?.(executed.summaries)
      }

      pendingToolOutputs = executed.outputs
      continue
    }

    const finalText = normalizedTextParts.join('\n')
    if (!finalText) throw new Error('Assistant returned an empty response')

    return {
      assistantMessage: finalText,
      requestPayload: usedPayload,
      responsePayload: lastResponsePayload,
      model: modelToUse,
      usage: aggregatedUsage ?? undefined,
      usedTools,
      toolCalls: toolSummaries.length ? toolSummaries : undefined,
      toolCallsRequested: requestedSummaries.length ? requestedSummaries : undefined,
      intermediateMessage: intermediateMessages.length
        ? intermediateMessages[intermediateMessages.length - 1]
        : undefined,
      intermediateMessages: intermediateMessages.length ? intermediateMessages : undefined,
    }
  }

  throw new Error('Assistant required too many tool call iterations')
}
